import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Bug, Palette, Zap, Server, FlaskConical, Tag,
  ArrowLeft, Search, Rss, X,
} from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { markChangelogVisited } from '../lib/useChangelogUnread';

const API = process.env.REACT_APP_BACKEND_URL;

/** Maps the prefix/emoji in a category label to a colored badge config. */
const CATEGORY_STYLES = [
  { id: 'nuovo',       match: /Nuovo|✨/i,       icon: Sparkles,     bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-800',  dot: 'bg-emerald-500' },
  { id: 'fix',         match: /Fix|🐞/i,         icon: Bug,          bg: 'bg-amber-50',    border: 'border-amber-200',   text: 'text-amber-800',    dot: 'bg-amber-500' },
  { id: 'ui',          match: /UI|🎨/i,          icon: Palette,      bg: 'bg-violet-50',   border: 'border-violet-200',  text: 'text-violet-800',   dot: 'bg-violet-500' },
  { id: 'performance', match: /Performance|⚡/i, icon: Zap,          bg: 'bg-yellow-50',   border: 'border-yellow-200',  text: 'text-yellow-800',   dot: 'bg-yellow-500' },
  { id: 'backend',     match: /Backend|⚙️|🛠️/i, icon: Server,       bg: 'bg-slate-50',    border: 'border-slate-200',   text: 'text-slate-800',    dot: 'bg-slate-500' },
  { id: 'test',        match: /Test|🧪/i,        icon: FlaskConical, bg: 'bg-sky-50',      border: 'border-sky-200',     text: 'text-sky-800',      dot: 'bg-sky-500' },
];

const DEFAULT_STYLE = { id: 'altro', icon: Tag, bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', dot: 'bg-slate-400' };

const styleFor = (label) => CATEGORY_STYLES.find(s => s.match.test(label)) || DEFAULT_STYLE;
const stripEmoji = (label) => label.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();

function fmtDate(iso) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

function fmtRelative(iso) {
  if (!iso) return '';
  const target = new Date(`${iso}T00:00:00`);
  const diff = Date.now() - target.getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return 'oggi';
  if (days === 1) return 'ieri';
  if (days < 7) return `${days} giorni fa`;
  if (days < 30) return `${Math.floor(days / 7)} settimane fa`;
  if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
  return `${Math.floor(days / 365)} anni fa`;
}

const isRecent = (iso, withinDays = 7) => {
  if (!iso) return false;
  return Date.now() - new Date(`${iso}T00:00:00`).getTime() < withinDays * 86400000;
};

/** Tiny inline-markdown renderer: **bold**, _italic_, `code`, [text](url).
 *  Sufficient for changelog body text; avoids pulling a 60kB markdown lib. */
function InlineMarkdown({ text }) {
  if (!text) return null;
  // Order matters: handle code first (so its content is not re-processed),
  // then links, then bold, then italic.
  const tokens = [];
  let rest = text;
  const patterns = [
    { re: /^`([^`]+)`/,                render: (m) => <code key={tokens.length} className="px-1 py-0.5 bg-slate-100 border border-slate-200 font-mono text-[0.8em] text-slate-800 rounded-sm">{m[1]}</code> },
    { re: /^\[([^\]]+)\]\(([^)]+)\)/,  render: (m) => <a key={tokens.length} href={m[2]} target="_blank" rel="noreferrer" className="underline decoration-slate-300 hover:text-slate-900">{m[1]}</a> },
    { re: /^\*\*([^*]+)\*\*/,          render: (m) => <strong key={tokens.length} className="font-semibold text-slate-900">{m[1]}</strong> },
    { re: /^_([^_]+)_/,                render: (m) => <em key={tokens.length} className="italic">{m[1]}</em> },
  ];
  while (rest.length > 0) {
    let matched = false;
    for (const p of patterns) {
      const m = rest.match(p.re);
      if (m) {
        tokens.push(p.render(m));
        rest = rest.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (matched) continue;
    // No pattern matched at index 0: consume up to next special char.
    const next = rest.search(/[`*_[]/);
    if (next === -1) {
      tokens.push(rest);
      break;
    } else if (next === 0) {
      tokens.push(rest[0]);
      rest = rest.slice(1);
    } else {
      tokens.push(rest.slice(0, next));
      rest = rest.slice(next);
    }
  }
  return <>{tokens}</>;
}

export default function ChangelogPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCats, setActiveCats] = useState(new Set());  // empty = all
  const [activeAnchor, setActiveAnchor] = useState(null);
  const location = useLocation();

  // SEO meta tags + page title + OG preview.
  useEffect(() => {
    document.title = 'Changelog · StoreOps';
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };
    const description = "Aggiornamenti, novità e fix della piattaforma StoreOps — controllo KPI, redditività e gestione operativa dei punti vendita.";
    ensureMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', description);
    ensureMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', 'Changelog · StoreOps');
    ensureMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', description);
    ensureMeta('meta[property="og:type"]', { property: 'og:type' }).setAttribute('content', 'website');
    ensureMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', window.location.href);
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card' }).setAttribute('content', 'summary');
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/changelog`)
      .then(res => {
        setData(res.data);
        // Marca la visita: il pallino "Novità" sulla sidebar sparisce
        // immediatamente (via custom event) e a refresh successivi (via
        // localStorage) finché non esce una release più recente.
        if (res.data?.last_updated) {
          markChangelogVisited(res.data.last_updated);
        }
      })
      .catch(err => setError(err.response?.data?.detail || err.message));
  }, []);

  // Scroll-spy: highlight the date in the TOC matching the topmost release.
  useEffect(() => {
    if (!data?.releases?.length) return undefined;
    const onScroll = () => {
      const sections = data.releases.map(r => document.getElementById(`r-${r.slug}`)).filter(Boolean);
      const fromTop = window.scrollY + 140;  // account for sticky header
      let active = sections[0]?.id?.replace(/^r-/, '') || null;
      for (const s of sections) {
        if (s.offsetTop <= fromTop) active = s.id.replace(/^r-/, '');
      }
      setActiveAnchor(active);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [data]);

  // Deep-link: if the URL has a hash like #2026-04-28, scroll into view.
  useEffect(() => {
    if (!data || !location.hash) return;
    const el = document.getElementById(`r-${location.hash.slice(1)}`);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [data, location.hash]);

  const copyShareLink = async (anchor) => {
    const url = anchor
      ? `${window.location.origin}${window.location.pathname}#${anchor}`
      : window.location.href.replace(/#.*$/, '');
    if (anchor) {
      // Update the URL hash so the permalink is reflected even if the
      // clipboard write fails (headless browsers, blocked permission, etc.).
      window.history.replaceState(null, '', `#${anchor}`);
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(anchor || 'all');
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard might be blocked */ }
  };

  const toggleCat = (id) => {
    setActiveCats(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearFilters = () => { setActiveCats(new Set()); setSearch(''); };

  // Filtering — preserve original release/category structure but drop items
  // (and entire empty categories/releases) that don't match.
  const filteredReleases = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    const noFilters = activeCats.size === 0 && !term;
    if (noFilters) return data.releases;
    return data.releases
      .map(rel => ({
        ...rel,
        categories: rel.categories
          .filter(cat => activeCats.size === 0 || activeCats.has(styleFor(cat.label).id))
          .map(cat => ({
            ...cat,
            items: cat.items.filter(it =>
              !term ||
              (it.title && it.title.toLowerCase().includes(term)) ||
              (it.body && it.body.toLowerCase().includes(term))
            ),
          }))
          .filter(cat => cat.items.length > 0),
      }))
      .filter(rel => rel.categories.length > 0);
  }, [data, search, activeCats]);

  const totalItems = data?.item_count ?? 0;
  const activeFiltered = filteredReleases.reduce(
    (acc, r) => acc + r.categories.reduce((a, c) => a + c.items.length, 0), 0,
  );
  const hasFilters = activeCats.size > 0 || search.length > 0;
  const latestSlug = data?.releases?.[0]?.slug;

  return (
    <div className="min-h-screen bg-white" data-testid="changelog-page">
      {/* Top bar — header pubblico condiviso con tabs Changelog/Backlog */}
      <PublicHeader
        actions={
          <a
            href={`${API}/api/changelog.rss`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-orange-600 px-2 sm:px-2.5 py-1 border border-slate-200 hover:border-orange-300 transition-colors"
            data-testid="changelog-rss-btn"
            title="Iscriviti al feed RSS"
            aria-label="Iscriviti al feed RSS"
          >
            <Rss className="w-3.5 h-3.5" /> <span className="hidden sm:inline">RSS</span>
          </a>
        }
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-[200px_1fr] gap-12 break-words">
        {/* TOC sidebar (desktop only) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24" data-testid="changelog-toc">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">Cronologia</p>
            <nav className="space-y-0.5 border-l border-slate-200">
              {data?.releases?.map(r => {
                const active = activeAnchor === r.slug;
                return (
                  <a
                    key={r.slug}
                    href={`#${r.slug}`}
                    className={`block pl-3 -ml-px py-1.5 text-xs border-l-2 transition-colors ${
                      active
                        ? 'border-slate-900 text-slate-900 font-semibold'
                        : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
                    }`}
                    data-testid={`toc-${r.slug}`}
                  >
                    {fmtDate(r.date)}
                  </a>
                );
              })}
            </nav>
          </div>
        </aside>

        <section>
          {/* Hero */}
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">Aggiornamenti prodotto</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-3">
              Cosa c'è di nuovo
            </h1>
            <p className="text-base text-slate-500 max-w-xl mb-6">
              Ogni miglioramento, fix e novità della piattaforma StoreOps.
            </p>
            {data && (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500" data-testid="changelog-stats">
                <span><strong className="text-slate-900 font-heading text-base mr-1">{data.release_count}</strong>release</span>
                <span><strong className="text-slate-900 font-heading text-base mr-1">{totalItems}</strong>aggiornamenti</span>
                {data.last_updated && (
                  <span>Ultimo update <span className="text-slate-700 font-medium">{fmtRelative(data.last_updated)}</span></span>
                )}
              </div>
            )}
          </div>

          {/* Search + filters */}
          <div className="mb-10 space-y-3" data-testid="changelog-filters">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca tra gli aggiornamenti…"
                className="w-full pl-10 pr-9 py-2.5 text-sm border border-slate-200 focus:border-slate-900 focus:outline-none transition-colors bg-white"
                data-testid="changelog-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900"
                  data-testid="changelog-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <FilterPill
                label="Tutte"
                active={activeCats.size === 0}
                onClick={() => setActiveCats(new Set())}
                testId="filter-pill-all"
              />
              {CATEGORY_STYLES.map(s => {
                const Icon = s.icon;
                const active = activeCats.has(s.id);
                return (
                  <FilterPill
                    key={s.id}
                    label={s.id.charAt(0).toUpperCase() + s.id.slice(1)}
                    icon={Icon}
                    active={active}
                    onClick={() => toggleCat(s.id)}
                    testId={`filter-pill-${s.id}`}
                  />
                );
              })}
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-slate-500 hover:text-slate-900 ml-2 underline decoration-slate-300"
                  data-testid="changelog-clear-filters"
                >
                  Azzera filtri
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {error && (
            <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="changelog-error">
              Impossibile caricare il changelog: {error}
            </div>
          )}

          {!data && !error && <SkeletonRelease />}

          {data && filteredReleases.length === 0 && (
            <div className="border border-slate-200 bg-slate-50 px-6 py-12 text-center" data-testid="changelog-empty">
              <p className="text-sm text-slate-500 mb-3">Nessun aggiornamento corrisponde ai filtri.</p>
              <button onClick={clearFilters} className="text-xs text-slate-700 underline">Azzera filtri</button>
            </div>
          )}

          {data && filteredReleases.length > 0 && (
            <div className="relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" aria-hidden="true" />
              <div className="space-y-14">
                {filteredReleases.map((release) => (
                  <ReleaseBlock
                    key={release.slug}
                    release={release}
                    isLatest={release.slug === latestSlug}
                    onCopyAnchor={() => copyShareLink(release.slug)}
                    copiedSlug={copied}
                    activeFilteredCount={activeFiltered}
                  />
                ))}
              </div>
              {hasFilters && (
                <p className="mt-8 pt-4 border-t border-slate-100 text-xs text-slate-500" data-testid="changelog-filter-summary">
                  {activeFiltered} di {totalItems} aggiornamenti visibili.
                </p>
              )}
            </div>
          )}

          <footer className="mt-20 pt-8 border-t border-slate-100">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="changelog-back-link">
              <ArrowLeft className="w-4 h-4" /> Torna alla piattaforma
            </Link>
          </footer>
        </section>
      </main>
    </div>
  );
}

function FilterPill({ label, icon: Icon, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border transition-colors ${
        active
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900'
      }`}
      data-testid={testId}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

function ReleaseBlock({ release, isLatest, onCopyAnchor, copiedSlug }) {
  return (
    <section
      className="relative pl-8 scroll-mt-20"
      id={`r-${release.slug}`}
      data-testid={`changelog-release-${release.date}`}
    >
      <span className="absolute left-0 top-2 w-4 h-4 bg-white border-2 border-slate-900 rounded-full" aria-hidden="true" />
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <a
          href={`#${release.slug}`}
          onClick={(e) => { e.preventDefault(); onCopyAnchor(); }}
          className="font-heading text-lg font-bold text-slate-900 tracking-tight hover:underline decoration-slate-300 decoration-2 underline-offset-4 cursor-pointer"
          title="Copia link a questa release"
          data-testid={`release-anchor-${release.slug}`}
        >
          <time dateTime={release.date}>{fmtDate(release.date)}</time>
        </a>
        {isLatest && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wider" data-testid="badge-latest">
            <Sparkles className="w-3 h-3" /> Più recente
          </span>
        )}
        {copiedSlug === release.slug && (
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Link copiato
          </span>
        )}
      </div>
      <div className="space-y-6">
        {release.categories.map((cat, i) => (
          <CategoryBlock key={cat.label || `cat-${i}`} category={cat} releaseDate={release.date} />
        ))}
      </div>
    </section>
  );
}

function CategoryBlock({ category, releaseDate }) {
  const style = styleFor(category.label);
  const Icon = style.icon;
  return (
    <div data-testid={`changelog-category-${style.id}`}>
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${style.bg} ${style.border} ${style.text} text-[11px] font-semibold uppercase tracking-wider mb-3`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{stripEmoji(category.label)}</span>
      </div>
      <ul className="space-y-2.5">
        {category.items.map((it, i) => (
          <li key={`${it.title || ''}-${i}`} className="flex gap-3 items-start text-sm leading-relaxed">
            <span className={`w-1.5 h-1.5 ${style.dot} rounded-full mt-[7px] flex-shrink-0`} aria-hidden="true" />
            <span className="text-slate-700 flex-1">
              {it.title && <span className="font-semibold text-slate-900"><InlineMarkdown text={it.title} /></span>}
              {it.title && ' — '}
              <span><InlineMarkdown text={it.body} /></span>
              {isRecent(releaseDate, 7) && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[9px] font-semibold uppercase tracking-wider align-middle" data-testid="badge-recent">
                  Novità
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SkeletonRelease() {
  return (
    <div className="space-y-6 animate-pulse" data-testid="changelog-skeleton">
      <div className="h-5 w-40 bg-slate-200" />
      <div className="space-y-3">
        <div className="h-4 w-24 bg-slate-100" />
        <div className="h-3 w-full bg-slate-100" />
        <div className="h-3 w-5/6 bg-slate-100" />
        <div className="h-3 w-3/4 bg-slate-100" />
      </div>
      <div className="space-y-3">
        <div className="h-4 w-20 bg-slate-100" />
        <div className="h-3 w-full bg-slate-100" />
        <div className="h-3 w-2/3 bg-slate-100" />
      </div>
    </div>
  );
}
